import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AppStateService } from './app-state.service';
import { LogService } from '../baseServices/logging/log.service';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';

export interface TimeoutScheduleOptions {
  /** Unique ID for this scheduled timeout. */
  id: string;
  /** When to fire */
  at: DateTimeType;
  /** Optional timezone ID for converting 'at' to absolute time. Assumes 'at' is in UTC if not provided. */
  timeZoneId?: string;
  /** If true (default), emit immediately if already past specified 'at' time. */
  fireIfPast?: boolean;
  /** Extra diagnostic context. */
  context?: Record<string, any>;
}

export interface TimeoutFiredEvent {
  id: string;
  at: Date;          // scheduled time
  firedAt: Date;     // actual fire time
  lateByMs: number;  // 0 if on-time or early (clamped), positive if late
  mode: TimeoutFiredMode;
  context?: Record<string, any>;
}

export enum TimeoutFiredMode {
  Scheduled = 'scheduled', // normal on-time firing
  Immediate = 'immediate', // fired immediately because scheduled time was in the past
  Resume = 'resume' // fired on app resume if missed while suspended
}

interface InternalEntry {
  opts: TimeoutScheduleOptions;
  atDate: Date; // Converted Date for efficient reuse
  handle: ReturnType<typeof setTimeout> | null;
}

/**
 * TimeoutSchedulerService
 *
 * Schedules single-instance timers and protects against app suspension:
 *  - One active timer per id; scheduling the same id replaces the previous timer.
 *  - Emits all fired events through the `timeout$` observable.
 *  - On app resume, detects and emits timeouts that were missed while the app was suspended.
 *
 * Usage:
 *  - Call `schedule()` with a unique id to register a timeout.
 *  - Subscribe to `timeout$` or use `getTimeoutById()` to receive TimeoutFiredEvent notifications.
 */
@Injectable({ providedIn: 'root' })
export class TimeoutSchedulerService implements OnDestroy {
  private readonly entries = new Map<string, InternalEntry>();
  private readonly MAX_DELAY = 0x7FFFFFFF; // 2,147,483,647 (~24.8 days)
  private readonly timeoutSubject = new Subject<TimeoutFiredEvent>();
  public readonly timeout$ = this.timeoutSubject.asObservable();
  private destroy$ = new Subject<void>();

  constructor(
    private appStateService: AppStateService,
    private logService: LogService
  ) {
    this.appStateService.getAppState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        if (s.isActive) this.handleResume();
      });
  }

  schedule({
    id,
    at,
    timeZoneId = 'UTC',
    fireIfPast = true,
    context = {}
  }: TimeoutScheduleOptions) {
    this.cancel(id);

    const atDate = DateTimeHelper.convertDatetimeTypeToDateType(at, timeZoneId);
    const nowMs = Date.now();
    const atMs = atDate.getTime();
    const delay = atMs - nowMs;

    if (delay <= 0) {
      if (fireIfPast) {
        this.emit({
          id,
          at: atDate,
          firedAt: new Date(),
          lateByMs: Math.max(-delay, 0),
          mode: TimeoutFiredMode.Immediate,
          context
        });
      } else {
        this.logService.debug('TimeoutScheduler.PastSkipped', { id, lateByMs: -delay });
      }
      return;
    }

    if (delay > this.MAX_DELAY) {
      this.logService.warn('TimeoutScheduler.DelayTooLarge', { id, delay, context });
      return;
    }

    const handle = setTimeout(() => {
      this.entries.delete(id);
      this.emit({ id, at: atDate, firedAt: new Date(), lateByMs: 0, mode: TimeoutFiredMode.Scheduled, context });
    }, delay);

    const opts: TimeoutScheduleOptions = { id, at, timeZoneId, fireIfPast, context };
    this.entries.set(id, { opts, atDate, handle });
    this.logService.debug('TimeoutScheduler.Scheduled', { id, inMs: delay, at: atMs, context });
  }

  cancel(id: string) {
    const entry = this.entries.get(id);
    if (!entry) return;
    if (entry.handle) clearTimeout(entry.handle);
    this.entries.delete(id);
    this.logService.debug('TimeoutScheduler.Cancelled', { id });
  }

  private cancelAll() {
    for (const id of this.entries.keys()) {
      this.cancel(id);
    }
  }

  /**
   * Returns an observable that emits only timeout events for the specified ID.
   * This eliminates the need to manually filter timeout$ by ID in consuming services.
   * 
   * @param id - The timeout ID to filter for
   * @returns Observable that emits TimeoutFiredEvent only for the specified ID
   */
  getTimeoutById(id: string): Observable<TimeoutFiredEvent> {
    return this.timeout$.pipe(
      filter(event => event.id === id)
    );
  }

  private handleResume() {
    const nowMs = Date.now();
    for (const [id, entry] of [...this.entries.entries()]) {
      const atMs = entry.atDate.getTime();

      if (atMs <= nowMs) {
        if (entry.handle) clearTimeout(entry.handle);
        this.entries.delete(id);
        this.emit({
          id,
          at: entry.atDate,
          firedAt: new Date(),
          lateByMs: nowMs - atMs,
          mode: TimeoutFiredMode.Resume,
          context: entry.opts.context
        });
      }
    }
  }

  private emit(evt: TimeoutFiredEvent) {
    this.logService.debug('TimeoutScheduler.Fired', { id: evt.id, mode: evt.mode, lateByMs: evt.lateByMs });
    this.timeoutSubject.next(evt);
  }

  ngOnDestroy(): void {
    this.cancelAll();
    this.destroy$.next();
    this.destroy$.complete();
    this.timeoutSubject.complete();
  }
}
